import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { Formik, Form } from 'formik';
import { FormLabel, Paper, Divider, Chip, Box, IconButton, Alert, DialogActions as MuiDialogActions, DialogContent as MuiDialogContent, DialogContentText, DialogTitle as MuiDialogTitle, Dialog as MuiDialog } from '@mui/material';
import { postApi, getApi } from 'views/services/api';
import { toast } from 'react-toastify';
import { hotelInputToUTC, getHotelInputDateTime } from 'utils/timeZone';
import { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HotelIcon from '@mui/icons-material/Hotel';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import InputAdornment from '@mui/material/InputAdornment';
import PropTypes from 'prop-types';
import { FormControl, InputLabel } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import * as Yup from 'yup';
import { Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MoneyIcon from '@mui/icons-material/Money';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import InfoIcon from '@mui/icons-material/Info';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Avatar } from '@mui/material';

// SISTEMA DE PUNTOS PARA CONFIRMACIÓN DE RESERVAS
const calculateReservationPoints = (stayType, isNewCustomer = false) => {
  const POINTS_CONFIG = {
    night: 50, // Puntos por noche completa
    partial: 25 // Puntos por tiempo parcial
  };

  let points = POINTS_CONFIG[stayType] || 0;

  // Bonus para clientes nuevos en su primera reserva confirmada
  if (isNewCustomer) {
    points += 20;
  }

  console.log(`Puntos calculados por reserva confirmada: ${points} (tipo: ${stayType}, nuevo: ${isNewCustomer})`);
  return points;
};

const getRewardLevel = (totalPoints) => {
  if (totalPoints >= 1000) {
    return {
      level: 'VIP',
      benefit: 'Noche gratis disponible',
      discount: 20,
      description: 'NIVEL VIP! Tienes una noche gratis disponible'
    };
  } else if (totalPoints >= 500) {
    return {
      level: 'Gold',
      benefit: '15% descuento + amenidades gratis',
      discount: 15,
      description: 'NIVEL GOLD! 15% descuento + amenidades gratis'
    };
  } else if (totalPoints >= 300) {
    return {
      level: 'Silver',
      benefit: '10% descuento + upgrade habitación',
      discount: 10,
      description: 'NIVEL SILVER! 10% descuento + upgrade de habitación'
    };
  } else if (totalPoints >= 100) {
    return {
      level: 'Bronze',
      benefit: '5% descuento próxima reserva',
      discount: 5,
      description: 'NIVEL BRONZE! 5% descuento en tu próxima reserva'
    };
  }

  const pointsToNextLevel = 100 - totalPoints;
  return {
    level: 'Regular',
    benefit: 'Acumula puntos para descuentos',
    discount: 0,
    description: `Te faltan ${pointsToNextLevel} puntos para obtener 5% descuento`
  };
};

// Esquema de validación específico para conversión de pre-reservas
const convertPreReservationSchema = Yup.object().shape({
  customerIdCard: Yup.string()
    .required('Cédula de identidad es requerida')
    .notOneOf(['Pendiente', 'pendiente', 'PENDIENTE'], 'Debe ingresar un número de cédula válido'),
  roomType: Yup.string().required('Tipo de habitación es requerido'),
  roomNo: Yup.string().required('Número de habitación es requerido'),
  totalAmount: Yup.number()
    .transform((value) => (isNaN(value) || value === null || value === undefined ? 0 : value))
    .min(0, 'El monto debe ser positivo'),
  checkInDate: Yup.string().required('Fecha de entrada es requerida'),
  checkOutDate: Yup.string().when('stayType', {
    is: 'night',
    then: (schema) => schema.required('Fecha de salida es requerida para estadías por noche'),
    otherwise: (schema) => schema.notRequired()
  }),
  paymentMethod: Yup.string().when(['showSplitPayments'], {
    is: (showSplitPayments) => !showSplitPayments,
    then: (schema) => schema.required('Método de pago es requerido'),
    otherwise: (schema) => schema.notRequired()
  }),
  advanceAmount: Yup.number()
    .transform((value) => (isNaN(value) || value === null || value === undefined ? 0 : value))
    .min(0, 'El anticipo debe ser positivo'),
  stayType: Yup.string().required('Tipo de estadía es requerido')
});

const ConvertPreReservation = ({ open, handleClose, preReservationData, onSuccess }) => {
  // Estados integrados
  const [roomNumbers, setRoomNumbers] = useState([]);
  const [roomData, setRoomData] = useState([]);
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [amount, setAmount] = useState(0);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [stayTypes, setStayTypes] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Estados para pagos múltiples
  const [splitPayments, setSplitPayments] = useState([]);
  const [showSplitPayments, setShowSplitPayments] = useState(false);

  // Promo Code States
  const [promoCode, setPromoCode] = useState(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoError, setPromoError] = useState('');
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(0);

  // Estados para Resolución de Conflictos
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);
  const [pendingResetForm, setPendingResetForm] = useState(null);

  const formikRef = useRef(null);

  const hotel = JSON.parse(localStorage.getItem('hotelData'));
  const currency = hotel?.currency || 'USD';

  // Función helper para obtener info del cliente desde pre-reservación
  const getCustomerInfo = (preReservation) => {
    if (preReservation?.customerId && typeof preReservation.customerId === 'object') {
      return {
        firstName: preReservation.customerId.firstName || preReservation.firstName,
        lastName: preReservation.customerId.lastName || preReservation.lastName,
        email: preReservation.customerId.email || preReservation.email,
        phone: preReservation.customerId.phoneNumber || preReservation.phoneNumber,
        idCardType: preReservation.customerId.idCardType || preReservation.idCardType || 'ID',
        idCard: preReservation.customerId.idcardNumber || preReservation.idNumber
      };
    }

    return {
      firstName: preReservation?.firstName || '',
      lastName: preReservation?.lastName || '',
      email: preReservation?.email || '',
      phone: preReservation?.phoneNumber || '',
      idCardType: preReservation?.idCardType || 'ID',
      idCard: preReservation?.idNumber || ''
    };
  };

  // Calcular fecha de salida automáticamente
  const calculateCheckOutDate = (checkInDate, stayType) => {
    const checkIn = new Date(checkInDate);
    const checkOut = new Date(checkIn);

    if (stayType === 'parcial') {
      checkOut.setHours(checkIn.getHours() + 3);
    } else {
      checkOut.setDate(checkIn.getDate() + 1);
      checkOut.setHours(12, 0, 0, 0); // 12:00 PM del día siguiente
    }

    return checkOut.toISOString().slice(0, 16);
  };

  // Valores iniciales basados en la pre-reservación
  const getInitialValues = () => {
    if (!preReservationData) return {};

    const customerInfo = getCustomerInfo(preReservationData);

    // Fecha actual corregida (asegurar que sea válida) usando zona horaria del hotel
    const currentDateTime = getHotelInputDateTime(10); // +10 minutos

    console.log('Fecha inicial generada:', currentDateTime);

    // Mapear correctamente el tipo de estadía (intentar priorizar el ID)
    let stayType = preReservationData.stayTypeId || (preReservationData.stayType === 'noche' ? 'night' : 'partial');

    // Auto-map legacy text strings to actual dynamic stayTypes IDs if they are loaded
    if (stayTypes && stayTypes.length > 0) {
      if (stayType === 'night') {
        const defaultNightly = stayTypes.find((t) => t.isNightly);
        if (defaultNightly) stayType = defaultNightly._id;
      } else if (stayType === 'partial') {
        const defaultPartial = stayTypes.find((t) => !t.isNightly && t.hours > 0);
        if (defaultPartial) stayType = defaultPartial._id;
      }
    }

    // Calcular fecha salida inicial
    let initialCheckOutDate = '';
    // Intenta calcular salida si conocemos el stayType
    if (stayType === 'partial') {
      initialCheckOutDate = getHotelInputDateTime(190);
    } else {
      initialCheckOutDate = '';
    }

    return {
      // Datos de la habitación
      roomType: '',
      roomNo: '',
      stayType: stayType,
      checkInDate: currentDateTime,
      checkOutDate: initialCheckOutDate,
      numberOfNights: 1,

      // Datos del cliente (pre-llenados)
      customerName: `${customerInfo.firstName} ${customerInfo.lastName}`,
      customerPhone: customerInfo.phone,
      customerEmail: customerInfo.email,
      customerIdCardType: customerInfo.idCardType,
      customerIdCard: customerInfo.idCard,

      // Datos de pago
      paymentMethod: '',
      advanceAmount: '',
      totalAmount: '',
      paymentCurrency: 'USD',
      exchangeRateUsed: 1,
      amountInForeignCurrency: '',

      // IDs importantes
      preReservationId: preReservationData._id,
      customerId: preReservationData.customerId?._id || preReservationData.customerId,

      // Estados para pagos múltiples (inicialmente vacíos)
      splitPayments: [],
      showSplitPayments: false
    };
  };

  // Función para obtener todas las habitaciones del hotel
  const fetchroomData = async () => {
    try {
      setIsLoadingRooms(true);
      console.log('Obteniendo todas las habitaciones del hotel...');

      const response = await getApi(`api/room/viewallrooms/${hotel?.hotelId}`);
      setRoomData(response?.data || []);

      console.log(`${response?.data?.length || 0} habitaciones cargadas del hotel`);
    } catch (error) {
      console.error('Error obteniendo habitaciones del hotel:', error);
      toast.error('No se pudieron cargar las habitaciones del hotel');
      setRoomData([]);
    } finally {
      setIsLoadingRooms(false);
    }
  };

  // Manejar cambio de tipo de habitación
  const handleRoomTypeChange = (selectedRoomType, setFieldValue) => {
    console.log('Cambiando tipo de habitación a:', selectedRoomType);

    // Filtrar habitaciones por tipo seleccionado
    const selectedRoomNumbers = roomData
      .filter((room) => room.roomType === selectedRoomType && room.bookingStatus !== 'active' && room.housekeepingStatus === 'clean')
      .map((room) => room.roomNo);

    setRoomNumbers(selectedRoomNumbers);
    console.log('Habitaciones disponibles para', selectedRoomType, ':', selectedRoomNumbers);

    // Actualizar valores del formulario
    setFieldValue('roomType', selectedRoomType);
    setFieldValue('roomNo', ''); // Reset room number selection
    setAmount(0); // Reset amount
  };

  // Calcular monto y fechas automáticamente
  const calculateAmount = (values, setFieldValue) => {
    const selectedRoom = roomData?.find((room) => room.roomNo === values.roomNo);

    if (!selectedRoom) {
      setAmount(0);
      setFieldValue('totalAmount', 0);
      return;
    }

    console.log('Calculando para:', selectedRoom.roomNo, 'Tipo:', values.stayType);

    // Calculates the correct checkout date based on Early or Late arrival
    const calculateCheckoutDate = (checkInMoment, nights) => {
      const hotelDataObj = JSON.parse(localStorage.getItem('hotelData') || '{}');

      let baseCheckoutTime = moment('14:00', 'HH:mm'); // Default 2 PM
      if (hotelDataObj.checkOutTime) {
        const timeObj = moment(hotelDataObj.checkOutTime, ['h:mm A']);
        if (timeObj.isValid()) baseCheckoutTime = timeObj;
      }

      // Extract time from checkIn to compare with hotel's checkout time
      const checkInTimeOnly = moment(checkInMoment.format('HH:mm'), 'HH:mm');

      let checkOutMoment;

      if (checkInTimeOnly.isBefore(baseCheckoutTime)) {
        // Arrived BEFORE checkout time -> Checkout is TODAY + (nights - 1)
        checkOutMoment = checkInMoment.clone().add(nights - 1, 'days');
      } else {
        // Arrived AFTER checkout time -> Checkout is TOMORROW + (nights - 1) 
        checkOutMoment = checkInMoment.clone().add(nights, 'days');
      }

      // Apply the exact hours of checkoutTime to the date
      checkOutMoment.set({
        hour: baseCheckoutTime.get('hour'),
        minute: baseCheckoutTime.get('minute'),
        second: 0
      });

      return checkOutMoment.format('YYYY-MM-DDTHH:mm');
    };

    let calculatedAmount = 0;
    const selectedStayType = stayTypes.find((type) => type._id === values.stayType);

    // Fallback to legacy logic/nightly if no specific type matching (or if 'night'/'partial' string is used solely)
    if (!selectedStayType) {
      if (values.stayType === 'partial') {
        // Lógica para tiempo parcial clásica
        calculatedAmount = selectedRoom.partialAmount || 0;

        // Auto-calcular fecha de salida (3 horas después de entrada)
        const checkInDate = new Date(values.checkInDate);
        const checkOutDate = new Date(checkInDate.getTime() + 3 * 60 * 60 * 1000);
        const formatted =
          checkOutDate.getFullYear() +
          '-' +
          String(checkOutDate.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(checkOutDate.getDate()).padStart(2, '0') +
          'T' +
          String(checkOutDate.getHours()).padStart(2, '0') +
          ':' +
          String(checkOutDate.getMinutes()).padStart(2, '0');

        setFieldValue('checkOutDate', formatted);
      } else {
        // Lógica para noche completa clásica
        if (values.checkInDate) {
          const checkInMoment = moment(values.checkInDate, 'YYYY-MM-DDTHH:mm');

          if (checkInMoment.isValid()) {
            const nights = parseInt(values.numberOfNights) || 1;
            const updatedCheckOutDate = calculateCheckoutDate(checkInMoment, nights);

            if (updatedCheckOutDate !== values.checkOutDate) {
              setFieldValue('checkOutDate', updatedCheckOutDate);
            }

            calculatedAmount = (selectedRoom.amount || 0) * nights;
          } else {
            calculatedAmount = selectedRoom.amount || 0;
          }
        }
      }
    } else {
      // Dynamic Stay Type Logic
      const pricingEntry = selectedRoom.pricing?.find((p) => p.stayTypeId === selectedStayType._id);
      const price = pricingEntry ? pricingEntry.amount : 0;
      calculatedAmount = price;

      if (selectedStayType.isNightly) {
        // Nightly Logic
        if (values.checkInDate) {
          const checkInMoment = moment(values.checkInDate, 'YYYY-MM-DDTHH:mm');
          if (checkInMoment.isValid()) {
            const nights = parseInt(values.numberOfNights) || 1;
            const updatedCheckOutDate = calculateCheckoutDate(checkInMoment, nights);

            if (updatedCheckOutDate !== values.checkOutDate && setFieldValue) {
              setFieldValue('checkOutDate', updatedCheckOutDate);
            }

            calculatedAmount = nights * price;
          }
        }
      } else if (selectedStayType.hours) {
        // Hourly Logic - Automatically set checkout time
        if (values.checkInDate) {
          const checkInMoment = moment(values.checkInDate, 'YYYY-MM-DDTHH:mm');

          if (checkInMoment.isValid()) {
            const checkOutMoment = checkInMoment.clone().add(selectedStayType.hours, 'hours');
            // Format for datetime-local input
            const formatted = checkOutMoment.format('YYYY-MM-DDTHH:mm');
            setFieldValue('checkOutDate', formatted);
          }
        }
      }
    }

    // Actualizar tanto el estado local como Formik
    setAmount(calculatedAmount);
    setFieldValue('totalAmount', calculatedAmount);
    setFieldValue('remainingAmount', calculatedAmount);

    console.log('Monto asignado a Formik:', calculatedAmount);
  };

  const fetchExchangeRates = async () => {
    try {
      const response = await getApi(`api/exchange/viewall/${hotel.hotelId}`);
      const activeRates = response.data?.filter((rate) => rate.isActive) || [];
      setExchangeRates(activeRates);
    } catch (error) {
      console.error('Error al obtener tasas de cambio:', error);
    }
  };

  const fetchStayTypes = async () => {
    try {
      const response = await getApi(`api/hotel/stay-types/list/${hotel.hotelId}`);
      if (response && response.status === 200) {
        setStayTypes(response.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch hotel settings', error);
    }
  };

  useEffect(() => {
    if (open && preReservationData) {
      fetchroomData();
      fetchExchangeRates();
      fetchStayTypes();
    }
  }, [open, preReservationData]);

  // Efecto para recalcular automáticamente
  useEffect(() => {
    // Solo recalcular si tenemos habitación seleccionada y formik está inicializado
    if (roomData.length > 0 && preReservationData) {
      console.log('Auto-recálculo por cambio en roomData');
    }
  }, [roomData, preReservationData]);

  // Efecto para mapear el viejo stayType cuando cargan los stayTypes
  useEffect(() => {
    if (stayTypes.length > 0 && preReservationData && formikRef.current) {
      const currentStayType = formikRef.current.values.stayType;
      if (currentStayType === 'night' || currentStayType === 'partial') {
        let mappedStayType = currentStayType;
        if (currentStayType === 'night') {
          const defaultNightly = stayTypes.find((t) => t.isNightly);
          if (defaultNightly) mappedStayType = defaultNightly._id;
        } else if (currentStayType === 'partial') {
          const defaultPartial = stayTypes.find((t) => !t.isNightly && t.hours > 0);
          if (defaultPartial) mappedStayType = defaultPartial._id;
        }

        if (mappedStayType !== currentStayType) {
          formikRef.current.setFieldValue('stayType', mappedStayType);
          console.log(`Auto-mapped stayType from ${currentStayType} to ${mappedStayType}`);
        }
      }
    }
  }, [stayTypes, preReservationData]);

  // Efecto para asegurar que totalAmount se actualice
  useEffect(() => {
    if (amount > 0) {
      console.log('Actualizando amount a:', amount);
    }
  }, [amount]);

  const finalAmount = Math.max(0, (parseFloat(amount) || 0) - (parseFloat(discountAmount) || 0));

  useEffect(() => {
    if (promoCode) {
      let calculatedDiscount = 0;
      if (promoCode.discountType === 'percentage') {
        calculatedDiscount = ((parseFloat(amount) || 0) * promoCode.discountValue) / 100;
      } else if (promoCode.discountType === 'fixed') {
        calculatedDiscount = promoCode.discountValue;
      }
      setDiscountAmount(calculatedDiscount);
    } else {
      setDiscountAmount(0);
    }
  }, [amount, promoCode]);

  useEffect(() => {
    if (formikRef.current) {
      const values = formikRef.current.values;
      const setFieldValue = formikRef.current.setFieldValue;
      
      const currentAdvance = parseFloat(values.advanceAmount) || 0;
      const currentRemaining = parseFloat(values.totalAmount) || 0;
      const expectedRemaining = Math.max(0, finalAmount - currentAdvance);

      if (currentAdvance > finalAmount) {
        setFieldValue('advanceAmount', finalAmount.toString());
        setFieldValue('totalAmount', '0.00');
      } else if (Math.abs(currentRemaining - expectedRemaining) > 0.01) {
        setFieldValue('totalAmount', expectedRemaining.toFixed(2));
      }

      if (!showSplitPayments && values.paymentCurrency && values.paymentCurrency !== 'USD') {
        const selectedRate = exchangeRates.find((rate) => rate.abbreviation === values.paymentCurrency)?.rate || 1;
        const converted = finalAmount * selectedRate;
        if (Math.abs(parseFloat(values.amountInForeignCurrency || 0) - converted) > 0.01) {
          setFieldValue('amountInForeignCurrency', converted.toFixed(2));
        }
      }
    }
  }, [finalAmount]);

  const applyPromoCode = async () => {
    setPromoError('');
    if (!promoInput.trim()) return;

    try {
      setIsValidatingPromo(true);
      const hotelId = JSON.parse(localStorage.getItem('hotelData'))?.hotelId;
      const response = await postApi('api/promocode/validate', { code: promoInput, hotelId });

      if (response.status === 200 && response.data.valid) {
        setPromoCode(response.data.data);
      } else {
        setPromoError(response.data.message || 'Código inválido');
      }
    } catch (error) {
      console.error("Error validating promo code:", error);
      setPromoError(error.response?.data?.message || 'Error al validar el código');
    } finally {
      setIsValidatingPromo(false);
    }
  };

  const removePromoCode = () => {
    setPromoCode(null);
    setPromoInput('');
  };

  // Manejar total y anticipo
  const handleTotalAmount = (advanceAmount, totalAmount, setFieldValue) => {
    if (advanceAmount < 0) return;

    const remaining = totalAmount - advanceAmount;

    if (!isNaN(advanceAmount) && advanceAmount <= totalAmount) {
      setFieldValue('advanceAmount', advanceAmount);
      setFieldValue('remainingAmount', remaining);
    } else {
      setFieldValue('advanceAmount', '');
      setFieldValue('remainingAmount', totalAmount);
      if (advanceAmount > totalAmount) {
        toast.warning('El anticipo no puede ser mayor que el monto total');
      }
    }
  };

  const handleAddSplitPayment = () => {
    const newPayment = {
      id: Date.now(),
      paymentMethod: 'cash',
      amount: '',
      currency: 'USD',
      exchangeRateUsed: 1,
      amountInForeignCurrency: 0,
      batchNumber: '',
      transactionNumber: ''
    };
    setSplitPayments([...splitPayments, newPayment]);
  };

  const handleRemoveSplitPayment = (paymentId) => {
    setSplitPayments(splitPayments.filter((p) => p.id !== paymentId));
  };

  const handleUpdateSplitPayment = (paymentId, field, value) => {
    setSplitPayments(
      splitPayments.map((payment) => {
        if (payment.id === paymentId) {
          const updatedPayment = { ...payment, [field]: value };

          if (field === 'currency') {
            if (value === 'USD') {
              updatedPayment.exchangeRateUsed = 1;
              updatedPayment.amountInForeignCurrency = 0;
            } else {
              const rate = exchangeRates.find((r) => r.abbreviation === value);
              if (rate) {
                updatedPayment.exchangeRateUsed = rate.rate;
                if (updatedPayment.amount) {
                  updatedPayment.amountInForeignCurrency = parseFloat(updatedPayment.amount) * rate.rate;
                }
              }
            }
          }

          if (field === 'amount' && updatedPayment.currency !== 'USD') {
            updatedPayment.amountInForeignCurrency = parseFloat(value || 0) * updatedPayment.exchangeRateUsed;
          }

          return updatedPayment;
        }
        return payment;
      })
    );
  };

  const getTotalPaid = () => {
    return splitPayments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
  };

  const areSplitPaymentsValid = (advanceAmount) => {
    if (!showSplitPayments) {
      console.log('✅ Split payments not enabled, validation passed');
      return true;
    }

    if (splitPayments.length === 0) {
      console.log('❌ No split payments added');
      return false;
    }

    const totalPaid = getTotalPaid();
    const advance = parseFloat(advanceAmount) || 0;

    console.log('🔍 Validating Split Payments:', {
      showSplitPayments,
      splitPaymentsCount: splitPayments.length,
      totalPaid: totalPaid.toFixed(2),
      advance: advance.toFixed(2),
      advanceAmount: advanceAmount
    });

    if (advance <= 0) {
      console.log('❌ Advance amount is zero or negative:', advance);
      return false;
    }

    const allPaymentsComplete = splitPayments.every((p, idx) => {
      const hasMethod = p.paymentMethod && p.paymentMethod !== '';
      const hasAmount = p.amount && parseFloat(p.amount) > 0;
      const isComplete = hasMethod && hasAmount;

      console.log(`  Payment #${idx + 1}:`, {
        method: p.paymentMethod,
        amount: p.amount,
        hasMethod,
        hasAmount,
        isComplete
      });

      return isComplete;
    });

    const totalsMatch = Math.abs(totalPaid - advance) < 0.01;
    const difference = (totalPaid - advance).toFixed(2);

    console.log('📊 Final Validation:', {
      allPaymentsComplete,
      totalsMatch,
      difference,
      isValid: allPaymentsComplete && totalsMatch
    });

    return allPaymentsComplete && totalsMatch;
  };

  // FUNCIÓN OPTIMIZADA PARA OTORGAR PUNTOS POR RESERVA CONFIRMADA
  const awardReservationPoints = async (customerId, stayType, preReservationId) => {
    try {
      console.log('Iniciando proceso de otorgamiento de puntos...');

      // Obtener información actual del cliente
      const customerResponse = await getApi(`api/customer/viewcustomer/${customerId}`);
      const customer = customerResponse?.data;

      if (!customer) {
        console.warn('No se encontró información del cliente');
        return { success: false, message: 'Cliente no encontrado' };
      }

      const currentPoints = customer.whatsappPoints || 0;

      // Verificar si es cliente nuevo (sin puntos previos)
      const isNewCustomer = currentPoints === 0;

      // Calcular puntos por la reserva
      const reservationPoints = calculateReservationPoints(stayType, isNewCustomer);
      const newTotalPoints = currentPoints + reservationPoints;

      console.log(`Puntos calculados: ${currentPoints} + ${reservationPoints} = ${newTotalPoints}`);

      // Actualizar puntos del cliente
      const updateResponse = await postApi('api/customer/updatecustomer', {
        customerId: customerId,
        whatsappPoints: newTotalPoints
      });

      if (updateResponse.status !== 200) {
        throw new Error('Error actualizando puntos del cliente');
      }

      // Crear transacción de puntos (opcional - si tienes este endpoint)
      const pointsTransactionData = {
        customerId: customerId,
        phoneNumber: customer.phoneNumber,
        type: 'earned',
        points: reservationPoints,
        description: `Puntos por reserva confirmada ${stayType === 'night' ? 'noche completa' : 'tiempo parcial'} - ${hotel?.hotelName}`,
        hotelId: hotel?.hotelId,
        reservationId: preReservationId // Para tracking
      };

      try {
        await postApi('api/points/create', pointsTransactionData);
        console.log('Transacción de puntos creada');
      } catch (pointsError) {
        console.warn('No se pudo crear transacción de puntos:', pointsError);
        // No fallar por esto - el endpoint puede no existir aún
      }

      // Actualizar pointsEarned en la pre-reserva (opcional)
      try {
        await postApi('api/preReservation/updatePointsEarned', {
          preReservationId: preReservationId,
          pointsEarned: reservationPoints
        });
        console.log('Pre-reserva actualizada con puntos ganados');
      } catch (updateError) {
        console.warn('No se pudo actualizar pointsEarned en pre-reserva:', updateError);
        // No fallar por esto
      }

      // Obtener nivel de recompensa
      const rewardLevel = getRewardLevel(newTotalPoints);

      console.log(`Puntos otorgados exitosamente`);
      console.log(`Nuevo nivel: ${rewardLevel.level}`);

      return {
        success: true,
        currentPoints,
        reservationPoints,
        newTotalPoints,
        rewardLevel,
        isNewCustomer
      };
    } catch (error) {
      console.error('Error otorgando puntos:', error);
      return {
        success: false,
        message: error.message || 'Error desconocido al otorgar puntos'
      };
    }
  };

  // Modificado: Ahora convertToReservation recibe resolutionChoice y usa postApi normal
  const convertToReservation = async (values, resetForm, resolutionChoice = null) => {
    setFormSubmitting(true);

    try {
      const customerInfo = getCustomerInfo(preReservationData);

      const toUTCString = (localDateStr) => {
        return hotelInputToUTC(localDateStr);
      };

      const formData = new FormData();

      // ... appending basic data
      formData.append('hotelId', hotel.hotelId);
      formData.append('createdBy', hotel._id);
      formData.append('cashRegisterId', localStorage.getItem('currentCashRegisterId') || '');

      // Datos de la habitación
      formData.append('roomNo', values.roomNo);
      formData.append('checkInDate', toUTCString(values.checkInDate));
      formData.append('checkOutDate', toUTCString(values.checkOutDate));
      formData.append('stayType', values.stayType);

      // Datos de pago
      if (showSplitPayments && splitPayments.length > 0) {
        formData.append('splitPayments', JSON.stringify(splitPayments));
      } else {
        formData.append('paymentMethod', values.paymentMethod || '');
        formData.append('paymentCurrency', values.paymentCurrency || '');
        formData.append('exchangeRateUsed', values.exchangeRateUsed || '');
        formData.append('amountInForeignCurrency', values.amountInForeignCurrency || '');
      }
      formData.append('advanceAmount', values.advanceAmount || 0);
      formData.append('totalAmount', finalAmount - (parseFloat(values.advanceAmount) || 0));

      if (promoCode) {
        formData.append('promoCodeId', promoCode._id);
        formData.append('discountAmount', parseFloat(discountAmount || 0).toFixed(2));
      }

      // Metadata de conversión
      formData.append('source', 'conversion');
      formData.append('preReservationId', values.preReservationId);

      // Si hay resolución de conflicto, lo enviamos
      if (resolutionChoice) {
        formData.append('resolutionChoice', resolutionChoice);
      }

      // Datos del cliente (extraídos de pre-reservación y formik)
      const customerArray = [
        {
          phoneNumber: customerInfo.phone,
          firstName: customerInfo.firstName,
          lastName: customerInfo.lastName,
          email: customerInfo.email,
          idCardType: customerInfo.idCardType, // Extraído de la data original
          idcardNumber: values.customerIdCard, // Obtenido de Formik (por si se ingresó en el paso final)
          address: '', // no disponible en pre-reservación
          idFile: '' // no disponible en pre-reservación
        }
      ];

      formData.append('customers', JSON.stringify(customerArray));

      console.log('Enviando conversión usando endpoint de reserva existente:', {
        roomNo: values.roomNo,
        totalAmount: amount,
        source: 'conversion',
        preReservationId: values.preReservationId
      });

      // Usar mismo endpoint que ReserveRoom.js
      const response = await postApi('api/customer/doreservation', formData);

      console.log('Respuesta del servidor:', response);

      if (response.status === 200) {
        // OTORGAR PUNTOS POR RESERVA CONFIRMADA
        let pointsMessage = '';

        if (values.customerId) {
          const pointsResult = await awardReservationPoints(values.customerId, values.stayType, values.preReservationId);

          if (pointsResult.success) {
            pointsMessage = `\n\nPUNTOS GANADOS:\n• +${pointsResult.reservationPoints} puntos por reserva confirmada\n• Total actual: ${pointsResult.newTotalPoints} puntos\n\n${pointsResult.rewardLevel.description}`;
          } else {
            console.warn('No se pudieron otorgar puntos:', pointsResult.message);
          }
        }

        const successMsg = 'Pre-reserva convertida exitosamente! Cliente notificado y reserva creada' + pointsMessage;

        toast.success(successMsg);
        handleClose();
        resetForm();

        // Llamar callback de éxito para refrescar la lista
        if (onSuccess) {
          onSuccess();
        }
      } else {
        throw new Error(response?.message || 'Error al convertir pre-reserva');
      }
    } catch (error) {
      console.error('Error convirtiendo pre-reserva:', error);

      let errorMsg = 'Error desconocido';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast.error('Error al convertir pre-reserva: ' + errorMsg);
    } finally {
      setFormSubmitting(false);
    }
  };

  if (!preReservationData) return null;

  const customerInfo = getCustomerInfo(preReservationData);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #e0e0e0',
          padding: '16px 24px',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <HotelIcon color="primary" />
          <Typography variant="h6">Convertir Pre-Reserva a Reserva</Typography>
        </Box>
        <IconButton onClick={handleClose} edge="end">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Información del cliente (solo lectura) */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: '#f8f9fa' }}>
          <Box display="flex" alignItems="center" mb={2}>
            <Chip color="info" label="INFO" size="small" sx={{ mr: 2 }} />
            <Typography variant="h6">Información de la Pre-Reservación</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Datos del Cliente
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  <strong>Cliente:</strong> {customerInfo.firstName} {customerInfo.lastName}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ ml: 3, mb: 1 }}>
                <strong>Teléfono:</strong> {customerInfo.phone}
              </Typography>
              <Typography variant="body2" sx={{ ml: 3, mb: 1 }}>
                <strong>Email:</strong> {customerInfo.email}
              </Typography>
              {customerInfo.idCard && (
                <Typography variant="body2" sx={{ ml: 3 }}>
                  <strong>ID:</strong> {customerInfo.idCard}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Detalles de la Reserva Solicitada
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Tipo de Estadía:</strong>
                <Chip
                  label={preReservationData.stayType === 'noche' ? 'Noche completa' : 'Tiempo parcial (3h)'}
                  color={preReservationData.stayType === 'noche' ? 'primary' : 'secondary'}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Fecha deseada:</strong> {new Date(preReservationData.checkInDate).toLocaleString()}
              </Typography>
              {preReservationData.promoCode && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Código de promoción:</strong>
                  <Chip
                    label={
                      preReservationData.promoCodeDiscount?.value
                        ? `${preReservationData.promoCode} (${
                            preReservationData.promoCodeDiscount.type === 'percentage'
                              ? preReservationData.promoCodeDiscount.value + '%'
                              : '$' + preReservationData.promoCodeDiscount.value
                          } desc.)`
                        : preReservationData.promoCode
                    }
                    color="success"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </Typography>
              )}
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Estado:</strong>
                <Chip label="Pendiente de conversión" color="warning" size="small" sx={{ ml: 1 }} />
              </Typography>
              {(preReservationData.roomTypeName || preReservationData.price) && (
                <>
                  {preReservationData.roomTypeName && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Habitación:</strong> {preReservationData.roomTypeName}
                    </Typography>
                  )}
                  {preReservationData.price && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Precio Solicitado:</strong> ${preReservationData.price}
                    </Typography>
                  )}
                </>
              )}
            </Grid>
          </Grid>
        </Paper>

        <Formik
          innerRef={formikRef}
          initialValues={getInitialValues()}
          validationSchema={convertPreReservationSchema}
          enableReinitialize={true}
          onSubmit={async (values, { resetForm }) => {
            console.log('🚀 FORMIK ONSUBMIT TRIGGERED!');

            if (showSplitPayments) {
              values.splitPayments = splitPayments;
              values.paymentMethod = 'split';
              values.totalPaid = getTotalPaid();
            } else {
              values.splitPayments = [];
            }

            // 1. CHEQUEO DE CONFLICTO DE IDENTIDAD
            setFormSubmitting(true);
            try {
              const customerInfo = getCustomerInfo(preReservationData);
              const response = await getApi(`api/customer/check-conflict?idcardNumber=${customerInfo.idCard}&hotelId=${hotel.hotelId}`);

              if (response?.data?.exists) {
                const dbCustomer = response.data.customerDetails;
                const dbName = `${dbCustomer.firstName} ${dbCustomer.lastName}`.trim().toLowerCase();
                const preName = `${customerInfo.firstName} ${customerInfo.lastName}`.trim().toLowerCase();

                // Solo mostrar diálogo si los nombres son diferentes
                if (dbName !== preName) {
                  setConflictData({ dbCustomer, preCustomer: customerInfo });
                  setPendingSubmitValues(values);
                  setPendingResetForm(() => resetForm);
                  setConflictDialogOpen(true);
                  setFormSubmitting(false);
                  return; // Pausar envío hasta que el usuario decida
                }
              }
              // Si no hay conflicto, proceder normalmente
              convertToReservation(values, resetForm);
            } catch (error) {
              console.error("Error al chequear conflicto", error);
              // Proceder de todos modos en caso de error de red para no bloquear
              convertToReservation(values, resetForm);
            }
          }}
        >
          {({ values, setFieldValue, errors, touched, isValid, handleBlur }) => {
            const selectedStayTypeObj = stayTypes?.find((type) => type._id === values.stayType);
            const isNightly = selectedStayTypeObj ? selectedStayTypeObj.isNightly : (values.stayType === 'night' || !values.stayType);
            const isPartial = selectedStayTypeObj ? (!selectedStayTypeObj.isNightly && selectedStayTypeObj.hours > 0) : values.stayType === 'partial';

            return (
              <Form>
                {/* Campos ocultos para Formik */}
                <input type="hidden" name="totalAmount" value={amount} />
                <input type="hidden" name="preReservationId" value={values.preReservationId} />
                <input type="hidden" name="customerIdCardType" value={values.customerIdCardType} />

                {/* Completar Identificación */}
                {values.customerIdCard === 'Pendiente' || !values.customerIdCard || errors.customerIdCard ? (
                  <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'error.main', bgcolor: '#fff5f5' }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <PersonIcon color="error" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1" color="error" fontWeight="bold">Completar Datos del Cliente</Typography>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel>Cédula de Identidad / Pasaporte <span style={{color: 'red'}}>*</span></FormLabel>
                          <TextField
                            name="customerIdCard"
                            value={values.customerIdCard === 'Pendiente' ? '' : values.customerIdCard}
                            onChange={(e) => setFieldValue('customerIdCard', e.target.value)}
                            size="small"
                            error={touched.customerIdCard && Boolean(errors.customerIdCard)}
                            helperText={(touched.customerIdCard && errors.customerIdCard) || "Requerido para crear la reserva"}
                            placeholder="Ej. 12345678"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>
                ) : null}

                {/* Selección de habitación */}
                <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" alignItems="center" mb={2}>
                    <Chip color="primary" label="HOTEL" size="small" sx={{ mr: 2 }} />
                    <Typography variant="h6">Selección de Habitación</Typography>
                  </Box>
                  <Divider sx={{ mb: 3 }} />

                  <Grid container spacing={3}>
                    {/* Selector de tipo de habitación */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel>Tipo de Habitación</FormLabel>
                        <Select
                          value={values.roomType}
                          onChange={(e) => {
                            handleRoomTypeChange(e.target.value, setFieldValue);
                          }}
                          size="small"
                          displayEmpty
                          disabled={isLoadingRooms}
                        >
                          <MenuItem value="">{isLoadingRooms ? 'Cargando tipos...' : 'Seleccione tipo de habitación'}</MenuItem>
                          {roomData
                            ?.filter((room, index, self) => self.findIndex((r) => r.roomType === room.roomType) === index)
                            .map((room, index) => (
                              <MenuItem key={index} value={room.roomType}>
                                {room.roomType}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Selector de número de habitación */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel>Número de Habitación</FormLabel>
                        <Select
                          value={values.roomNo}
                          onChange={(e) => {
                            const selectedRoomNo = e.target.value;
                            setFieldValue('roomNo', selectedRoomNo);

                            // Inmediatamente calcular
                            if (selectedRoomNo) {
                              calculateAmount({ ...values, roomNo: selectedRoomNo }, setFieldValue);
                            } else {
                              setAmount(0);
                            }
                          }}
                          size="small"
                          displayEmpty
                          disabled={!values.roomType || roomNumbers.length === 0}
                        >
                          <MenuItem value="">
                            {!values.roomType
                              ? 'Primero seleccione tipo'
                              : roomNumbers.length === 0
                                ? 'No hay habitaciones de este tipo'
                                : 'Seleccione número de habitación'}
                          </MenuItem>
                          {roomNumbers.map((roomNumber) => {
                            const roomInfo = roomData.find((room) => room.roomNo === roomNumber);

                            return (
                              <MenuItem key={roomNumber} value={roomNumber}>
                                <Box>
                                  <Typography variant="body2" fontWeight="bold">
                                    {roomNumber} - {roomInfo?.roomType}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Select>
                        {errors.roomNo && touched.roomNo && (
                          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                            {errors.roomNo}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Selector de tipo de estadía */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel>Tipo de Estadía</FormLabel>
                        <Select
                          name="stayType"
                          value={values.stayType}
                          onChange={(e) => {
                            setFieldValue('stayType', e.target.value);
                            calculateAmount({ ...values, stayType: e.target.value }, setFieldValue);
                          }}
                          onBlur={handleBlur}
                          error={touched.stayType && Boolean(errors.stayType)}
                        >
                          {stayTypes &&
                            stayTypes.length > 0 &&
                            stayTypes.map((type) => (
                              <MenuItem key={type._id} value={type._id}>
                                {type.label} ({type.isNightly ? 'Nightly' : `${type.hours}h`})
                              </MenuItem>
                            ))}
                          {/* Fallback for old pre-reservations that might not match a valid active stayType id */}
                          {!stayTypes.some((type) => type._id === values.stayType) && values.stayType && (
                            <MenuItem value={values.stayType}>
                              {values.stayType === 'night' ? 'Noche completa (Heredado)' : values.stayType === 'partial' ? 'Tiempo parcial (Heredado)' : values.stayType}
                            </MenuItem>
                          )}
                        </Select>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {isPartial
                            ? `Se calculan automáticamente ${selectedStayTypeObj ? selectedStayTypeObj.hours : 3} horas desde la entrada`
                            : 'Seleccione fecha de salida para calcular días'}
                        </Typography>
                      </FormControl>
                    </Grid>

                    {/* Input field for Number of Nights (only for Nightly stays) */}
                    {isNightly && (
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <FormLabel>
                            <CalendarTodayIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                            Número de Noches
                          </FormLabel>
                          <TextField
                            name="numberOfNights"
                            type="number"
                            size="small"
                            fullWidth
                            InputProps={{ inputProps: { min: 1 } }}
                            value={values.numberOfNights || 1}
                            onChange={(e) => {
                              const nights = Math.max(1, parseInt(e.target.value) || 1);
                              setFieldValue('numberOfNights', nights);
                              if (values.roomNo) {
                                calculateAmount({ ...values, numberOfNights: nights }, setFieldValue);
                              }
                            }}
                            sx={{ bgcolor: 'white' }}
                          />
                        </FormControl>
                      </Grid>
                    )}

                    {/* Reordered: 1. Fecha de Entrada */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel>Fecha de Entrada</FormLabel>
                        <TextField
                          value={values.checkInDate}
                          onChange={(e) => {
                            let newCheckInDate = e.target.value;

                            if (newCheckInDate && isNightly) {
                              const selectedMoment = moment(newCheckInDate, 'YYYY-MM-DDTHH:mm');
                              const todayMoment = moment().startOf('day');
                              const selectedDateMoment = selectedMoment.clone().startOf('day');

                              // Si la fecha seleccionada es en el futuro (después de hoy)
                              if (selectedDateMoment.isAfter(todayMoment)) {
                                const hotelDataObj = JSON.parse(localStorage.getItem('hotelData') || '{}');
                                let baseCheckinTime = moment('14:00', 'HH:mm'); // Default 2 PM

                                if (hotelDataObj.checkInTime) {
                                  const timeObj = moment(hotelDataObj.checkInTime, ['h:mm A', 'HH:mm']);
                                  if (timeObj.isValid()) baseCheckinTime = timeObj;
                                }

                                // Aplicar la hora de check-in del hotel a la fecha seleccionada
                                selectedMoment.set({
                                  hour: baseCheckinTime.get('hour'),
                                  minute: baseCheckinTime.get('minute'),
                                  second: 0
                                });

                                newCheckInDate = selectedMoment.format('YYYY-MM-DDTHH:mm');
                              }
                            }

                            setFieldValue('checkInDate', newCheckInDate);

                            // Recalcular automáticamente al cambiar fecha entrada
                            if (values.roomNo && values.stayType) {
                              calculateAmount({ ...values, checkInDate: newCheckInDate }, setFieldValue);
                            }
                          }}
                          size="small"
                          type="datetime-local"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarTodayIcon color="primary" fontSize="small" />
                              </InputAdornment>
                            )
                          }}
                          error={errors.checkInDate && touched.checkInDate}
                          helperText={errors.checkInDate && touched.checkInDate && errors.checkInDate}
                        />
                      </FormControl>
                    </Grid>

                    {/* Reordered: 2. Fecha de Salida */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel>Fecha de Salida</FormLabel>
                        <TextField
                          value={values.checkOutDate}
                          onChange={(e) => {
                            const newCheckOutDate = e.target.value;
                            setFieldValue('checkOutDate', newCheckOutDate);

                            // Solo recalcular si es noche completa
                            if (values.stayType === 'night' && values.roomNo) {
                              calculateAmount({ ...values, checkOutDate: newCheckOutDate }, setFieldValue);
                            }
                          }}
                          size="small"
                          type="datetime-local"
                          disabled={isPartial || isNightly} // Deshabilitado para tiempo parcial o noche controlada automáticamente
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarTodayIcon color={(isPartial || isNightly) ? 'disabled' : 'primary'} fontSize="small" />
                              </InputAdornment>
                            ),
                            sx: {
                              bgcolor: (isPartial || isNightly) ? '#f5f5f5' : 'transparent'
                            }
                          }}
                          inputProps={{
                            min: values.checkInDate // Fecha salida debe ser posterior a entrada
                          }}
                          error={errors.checkOutDate && touched.checkOutDate}
                          helperText={
                            isPartial
                              ? `Se calcula automáticamente (+${selectedStayTypeObj ? selectedStayTypeObj.hours : 3} horas)`
                              : isNightly
                                ? 'Calculado usando nro de noches y checkout hotel'
                                : errors.checkOutDate && touched.checkOutDate
                                  ? errors.checkOutDate
                                  : 'Seleccione para calcular días'
                          }
                        />
                      </FormControl>
                    </Grid>

                    {/* Reordered: 3. Campo visual de monto */}
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <FormLabel>Monto Total</FormLabel>
                        <TextField
                          value={values.roomNo ? `${currency} ${amount}` : 'Seleccione habitación'}
                          size="small"
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AttachMoneyIcon color={values.roomNo && amount > 0 ? 'success' : 'disabled'} fontSize="small" />
                              </InputAdornment>
                            ),
                            sx: {
                              bgcolor: values.roomNo && amount > 0 ? '#f0f9ff' : '#f5f5f5',
                              '& .MuiInputBase-input': {
                                fontWeight: values.roomNo && amount > 0 ? 'bold' : 'normal',
                                color: values.roomNo && amount > 0 ? '#059669' : '#6b7280'
                              }
                            }
                          }}
                        />
                      </FormControl>
                    </Grid>

                    {/* Mostrar estado actual del cálculo */}
                    <Grid item xs={12}>
                      {!values.roomNo ? (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            <strong>Seleccione una habitación</strong>
                            <br />
                            Primero debe seleccionar el tipo de habitación y luego el número específico para calcular los precios.
                          </Typography>
                        </Alert>
                      ) : (
                        <Alert severity={isPartial ? 'info' : 'success'} sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            <strong>{isPartial ? 'Estadía Parcial:' : 'Estadía por Noche:'}</strong>
                            <br />
                            <strong>Habitación:</strong> {values.roomNo} ({values.roomType})
                            <br />
                            {isPartial
                              ? `Precio fijo: ${currency} ${amount} por exactamente ${selectedStayTypeObj ? selectedStayTypeObj.hours : 3} horas. La fecha de salida (${values.checkOutDate ? new Date(values.checkOutDate).toLocaleString() : 'calculando...'
                              }) se establece automáticamente.`
                              : values.checkInDate && values.checkOutDate
                                ? `Precio: ${currency} ${amount} por ${Math.ceil(
                                  (new Date(values.checkOutDate) - new Date(values.checkInDate)) / (1000 * 3600 * 24)
                                )} día(s). Desde ${new Date(values.checkInDate).toLocaleString()} hasta ${new Date(
                                  values.checkOutDate
                                ).toLocaleString()}.`
                                : `Precio base: ${currency} ${amount} por noche. Seleccione la fecha de salida para calcular el total por múltiples días.`}
                          </Typography>
                        </Alert>
                      )}
                    </Grid>
                  </Grid>
                </Paper>

                {/* Información de pago - REDISEÑADO CON PAGOS MÚLTIPLES */}
                <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
                  <Box
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      p: 2,
                      color: 'white',
                      mb: 3,
                      borderRadius: 2
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48 }}>
                        <CreditCardIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="bold">
                          Información de Pago
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          Configure el método de pago para esta reserva
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* ========== SELECTOR DE MODO DE PAGO ========== */}
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Seleccione el tipo de pago:
                    </Typography>

                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {/* Opción: Pago Simple */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={!showSplitPayments ? 6 : 1}
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            border: !showSplitPayments ? '3px solid #667eea' : '2px solid #e0e0e0',
                            bgcolor: !showSplitPayments ? '#f3f4ff' : 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6
                            }
                          }}
                          onClick={() => {
                            if (showSplitPayments) {
                              setShowSplitPayments(false);
                              setSplitPayments([]);
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar
                              sx={{
                                bgcolor: !showSplitPayments ? '#667eea' : '#e0e0e0',
                                width: 56,
                                height: 56
                              }}
                            >
                              <CreditCardIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box flex={1}>
                              <Typography variant="h6" fontWeight="bold" color={!showSplitPayments ? 'primary.main' : 'text.secondary'}>
                                Pago Simple
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Un solo método de pago
                              </Typography>
                            </Box>
                            {!showSplitPayments && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />}
                          </Box>
                        </Paper>
                      </Grid>

                      {/* Opción: Pagos Múltiples */}
                      <Grid item xs={12} md={6}>
                        <Paper
                          elevation={showSplitPayments ? 6 : 1}
                          sx={{
                            p: 3,
                            cursor: 'pointer',
                            border: showSplitPayments ? '3px solid #4caf50' : '2px solid #e0e0e0',
                            bgcolor: showSplitPayments ? '#f1f8f4' : 'white',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 6
                            }
                          }}
                          onClick={() => {
                            if (!showSplitPayments) {
                              setShowSplitPayments(true);
                              // Auto-llenar anticipo
                              if (!values.advanceAmount && finalAmount > 0) {
                                setFieldValue('advanceAmount', finalAmount.toString());
                              }
                              // Inicializar con un pago vacío
                              setSplitPayments([
                                {
                                  id: Date.now(),
                                  paymentMethod: '',
                                  amount: '',
                                  currency: 'USD',
                                  exchangeRateUsed: 1,
                                  amountInForeignCurrency: 0,
                                  batchNumber: '',
                                  transactionNumber: ''
                                }
                              ]);
                            }
                          }}
                        >
                          <Box display="flex" alignItems="center" gap={2} mb={2}>
                            <Avatar
                              sx={{
                                bgcolor: showSplitPayments ? '#4caf50' : '#e0e0e0',
                                width: 56,
                                height: 56
                              }}
                            >
                              <MoneyIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box flex={1}>
                              <Typography variant="h6" fontWeight="bold" color={showSplitPayments ? 'success.main' : 'text.secondary'}>
                                Pagos Múltiples
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Dividir en varios pagos
                              </Typography>
                            </Box>
                            {showSplitPayments && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />}
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* ========== PAGO SIMPLE ========== */}
                  {!showSplitPayments && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
                        Configuración de Pago Simple
                      </Typography>

                      <Grid container spacing={3}>
                        {/* Método de Pago */}
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
                              <CreditCardIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                              Método de Pago
                            </FormLabel>
                            <Select
                              name="paymentMethod"
                              value={values.paymentMethod || ''}
                              onChange={(e) => setFieldValue('paymentMethod', e.target.value)}
                              size="small"
                              displayEmpty
                              sx={{ bgcolor: 'white' }}
                            >
                              <MenuItem value="">
                                <em>Seleccionar después</em>
                              </MenuItem>
                              <MenuItem value="cash">💵 Efectivo</MenuItem>
                              <MenuItem value="card">💳 Tarjeta</MenuItem>
                              <MenuItem value="transfer">🏦 Transferencia</MenuItem>
                              <MenuItem value="zelle">💸 Zelle</MenuItem>
                              <MenuItem value="other">📝 Otro</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Moneda de Pago */}
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
                              <SwapHorizIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                              Moneda de Pago
                            </FormLabel>
                            <Select
                              value={values.paymentCurrency || 'USD'}
                              onChange={(e) => {
                                const selectedCurrency = e.target.value;
                                const selectedRate = exchangeRates.find((rate) => rate.abbreviation === selectedCurrency)?.rate || 1;
                                const converted = finalAmount * selectedRate;
                                setFieldValue('paymentCurrency', selectedCurrency);
                                setFieldValue('exchangeRateUsed', selectedRate);
                                setFieldValue('amountInForeignCurrency', converted.toFixed(2));
                              }}
                              size="small"
                              sx={{ bgcolor: 'white' }}
                            >
                              <MenuItem value="USD">USD (Dólar)</MenuItem>
                              {exchangeRates.map((rate) => (
                                <MenuItem key={rate._id} value={rate.abbreviation}>
                                  {rate.abbreviation} - {rate.currency}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>

                        {/* Mostrar conversión si no es USD */}
                        {values.paymentCurrency && values.paymentCurrency !== 'USD' && (
                          <>
                            <Grid item xs={12} md={6}>
                              <Alert severity="info" icon={<SwapHorizIcon />}>
                                <Typography variant="body2">
                                  <strong>Tasa de Cambio:</strong> 1 USD = {values.exchangeRateUsed} {values.paymentCurrency}
                                </Typography>
                              </Alert>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Alert severity="success" icon={<AttachMoneyIcon />}>
                                <Typography variant="body2">
                                  <strong>Total en {values.paymentCurrency}:</strong> {values.amountInForeignCurrency}
                                </Typography>
                              </Alert>
                            </Grid>
                          </>
                        )}

                        {/* Campos adicionales según método de pago */}
                        {values.paymentMethod === 'card' && (
                          <>
                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Número de Lote</FormLabel>
                                <TextField
                                  name="batchNumber"
                                  value={values.batchNumber}
                                  onChange={(e) => setFieldValue('batchNumber', e.target.value)}
                                  placeholder="Ingrese el número de lote"
                                  size="small"
                                  sx={{ bgcolor: 'white' }}
                                />
                              </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <FormControl fullWidth>
                                <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Número de Transacción</FormLabel>
                                <TextField
                                  name="transactionNumber"
                                  value={values.transactionNumber}
                                  onChange={(e) => setFieldValue('transactionNumber', e.target.value)}
                                  placeholder="Ingrese el número de transacción"
                                  size="small"
                                  sx={{ bgcolor: 'white' }}
                                />
                              </FormControl>
                            </Grid>
                          </>
                        )}

                        {(values.paymentMethod === 'transfer' || values.paymentMethod === 'zelle') && (
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <FormLabel sx={{ mb: 1, fontWeight: 600 }}>Número de Transacción</FormLabel>
                              <TextField
                                name="transactionNumber"
                                value={values.transactionNumber}
                                onChange={(e) => setFieldValue('transactionNumber', e.target.value)}
                                placeholder="Ingrese el número de transacción"
                                size="small"
                                sx={{ bgcolor: 'white' }}
                              />
                            </FormControl>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  )}

                  {/* ========== PAGOS MÚLTIPLES ========== */}
                  {showSplitPayments && (
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                        Configuración de Pagos Múltiples
                      </Typography>

                      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
                        Configure múltiples métodos de pago. La suma total debe coincidir con el anticipo.
                      </Alert>

                      {/* Lista de Pagos */}
                      <Stack spacing={2} sx={{ mb: 3 }}>
                        {splitPayments.map((payment, index) => (
                          <Paper
                            key={payment.id}
                            elevation={2}
                            sx={{
                              p: 2.5,
                              border: '2px solid',
                              borderColor: payment.amount && payment.paymentMethod ? 'success.light' : 'grey.300',
                              bgcolor: payment.amount && payment.paymentMethod ? '#f9fafb' : 'white'
                            }}
                          >
                            {/* Header del Pago */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                  label={`Pago ${index + 1}`}
                                  color={payment.amount && payment.paymentMethod ? 'success' : 'default'}
                                  size="small"
                                />
                                {payment.amount && payment.paymentMethod && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />}
                              </Box>
                              {splitPayments.length > 1 && (
                                <IconButton size="small" color="error" onClick={() => handleRemoveSplitPayment(payment.id)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              )}
                            </Box>

                            {/* Campos del Pago */}
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                  <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Método</FormLabel>
                                  <Select
                                    value={payment.paymentMethod}
                                    onChange={(e) => handleUpdateSplitPayment(payment.id, 'paymentMethod', e.target.value)}
                                    displayEmpty
                                  >
                                    <MenuItem value="">Seleccionar</MenuItem>
                                    <MenuItem value="cash">💵 Efectivo</MenuItem>
                                    <MenuItem value="card">💳 Tarjeta</MenuItem>
                                    <MenuItem value="transfer">🏦 Transferencia</MenuItem>
                                    <MenuItem value="zelle">💸 Zelle</MenuItem>
                                    <MenuItem value="other">📝 Otro</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                  <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Moneda</FormLabel>
                                  <Select
                                    value={payment.currency}
                                    onChange={(e) => handleUpdateSplitPayment(payment.id, 'currency', e.target.value)}
                                  >
                                    <MenuItem value="USD">USD</MenuItem>
                                    {exchangeRates.map((rate) => (
                                      <MenuItem key={rate._id} value={rate.abbreviation}>
                                        {rate.abbreviation}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>

                              <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                  <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Monto (USD)</FormLabel>
                                  <TextField
                                    type="number"
                                    value={payment.amount}
                                    onChange={(e) => handleUpdateSplitPayment(payment.id, 'amount', e.target.value)}
                                    placeholder="0.00"
                                    InputProps={{
                                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                                    }}
                                    size="small"
                                  />
                                </FormControl>
                              </Grid>
                            </Grid>

                            {/* Campos adicionales según método de pago */}
                            {(payment.paymentMethod === 'transfer' || payment.paymentMethod === 'zelle') && (
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12}>
                                  <FormControl fullWidth size="small">
                                    <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Número de Transacción</FormLabel>
                                    <TextField
                                      value={payment.transactionNumber}
                                      onChange={(e) => handleUpdateSplitPayment(payment.id, 'transactionNumber', e.target.value)}
                                      placeholder="Ingrese el número de transacción"
                                      size="small"
                                    />
                                  </FormControl>
                                </Grid>
                              </Grid>
                            )}

                            {payment.paymentMethod === 'card' && (
                              <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth size="small">
                                    <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Número de Lote</FormLabel>
                                    <TextField
                                      value={payment.batchNumber}
                                      onChange={(e) => handleUpdateSplitPayment(payment.id, 'batchNumber', e.target.value)}
                                      placeholder="Ingrese el número de lote"
                                      size="small"
                                    />
                                  </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                  <FormControl fullWidth size="small">
                                    <FormLabel sx={{ mb: 0.5, fontSize: '0.875rem' }}>Número de Transacción</FormLabel>
                                    <TextField
                                      value={payment.transactionNumber}
                                      onChange={(e) => handleUpdateSplitPayment(payment.id, 'transactionNumber', e.target.value)}
                                      placeholder="Ingrese el número de transacción"
                                      size="small"
                                    />
                                  </FormControl>
                                </Grid>
                              </Grid>
                            )}

                            <Grid container spacing={2} sx={{ mt: 1 }}>
                              {/* Mostrar equivalencia si no es USD */}
                              {payment.currency !== 'USD' && payment.amount && parseFloat(payment.amount) > 0 && (
                                <Grid item xs={12}>
                                  <Alert severity="info" sx={{ py: 0.5 }}>
                                    <Typography variant="caption">
                                      💱 Equivalente:{' '}
                                      <strong>
                                        {payment.currency} {payment.amountInForeignCurrency.toFixed(2)}
                                      </strong>{' '}
                                      (Tasa: {payment.exchangeRateUsed})
                                    </Typography>
                                  </Alert>
                                </Grid>
                              )}
                            </Grid>
                          </Paper>
                        ))}
                      </Stack>

                      {/* Botón Agregar Pago */}
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddSplitPayment}
                        fullWidth
                        sx={{
                          mb: 3,
                          borderStyle: 'dashed',
                          borderWidth: 2,
                          py: 1.5
                        }}
                      >
                        Agregar Otro Método de Pago
                      </Button>

                      {/* Resumen de Pagos Múltiples */}
                      <Paper elevation={3} sx={{ p: 2.5, bgcolor: '#fafafa' }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          <ReceiptIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                          Resumen de Pagos
                        </Typography>
                        <Divider sx={{ my: 1.5 }} />

                        <Grid container spacing={1.5}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Total de Pagos:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} textAlign="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={
                                Math.abs(getTotalPaid() - parseFloat(values.advanceAmount || 0)) < 0.01 && getTotalPaid() > 0
                                  ? 'success.main'
                                  : 'error.main'
                              }
                            >
                              ${getTotalPaid().toFixed(2)}
                            </Typography>
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Anticipo Requerido:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} textAlign="right">
                            <Typography variant="body2" fontWeight="bold">
                              ${parseFloat(values.advanceAmount || 0).toFixed(2)}
                            </Typography>
                          </Grid>

                          <Grid item xs={12}>
                            <Divider />
                          </Grid>

                          <Grid item xs={6}>
                            <Typography variant="body2" fontWeight="bold">
                              Diferencia:
                            </Typography>
                          </Grid>
                          <Grid item xs={6} textAlign="right">
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={
                                Math.abs(getTotalPaid() - parseFloat(values.advanceAmount || 0)) < 0.01 ? 'success.main' : 'warning.main'
                              }
                            >
                              ${Math.abs(parseFloat(values.advanceAmount || 0) - getTotalPaid()).toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>

                        {/* Alerts de Validación */}
                        <Box sx={{ mt: 2 }}>
                          {getTotalPaid() === 0 && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              <Typography variant="caption">⚠️ Ingrese los montos en cada pago</Typography>
                            </Alert>
                          )}

                          {getTotalPaid() > 0 && Math.abs(getTotalPaid() - parseFloat(values.advanceAmount || 0)) >= 0.01 && (
                            <Alert severity="warning" sx={{ mb: 1 }}>
                              <Typography variant="caption" fontWeight="bold" display="block">
                                ⚠️ Acción Requerida:
                              </Typography>
                              <Typography variant="caption">
                                {getTotalPaid() < parseFloat(values.advanceAmount || 0)
                                  ? `Faltan $${(parseFloat(values.advanceAmount || 0) - getTotalPaid()).toFixed(2)} por distribuir`
                                  : `Sobran $${(getTotalPaid() - parseFloat(values.advanceAmount || 0)).toFixed(2)} en los pagos`}
                              </Typography>
                            </Alert>
                          )}

                          {getTotalPaid() > 0 &&
                            Math.abs(getTotalPaid() - parseFloat(values.advanceAmount || 0)) < 0.01 &&
                            splitPayments.every((p) => p.paymentMethod && parseFloat(p.amount) > 0) && (
                              <Alert severity="success" icon={<CheckCircleIcon />}>
                                <Typography variant="caption" fontWeight="bold">
                                  ✅ Pagos validados correctamente. Puede confirmar la reserva.
                                </Typography>
                              </Alert>
                            )}
                        </Box>
                      </Paper>
                    </Box>
                  )}

                  <Divider sx={{ my: 4 }} />

                  {/* ========== SECCIÓN DE MONTOS TOTALES ========== */}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 2 }}>
                      Resumen de Montos
                    </Typography>

                    <Grid container spacing={2}>
                      {/* Subtotal Original */}
                      <Grid item xs={12} md={3}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Subtotal
                          </Typography>
                          <Typography variant="h6" fontWeight="bold" color="text.secondary" sx={{ textDecoration: promoCode ? 'line-through' : 'none' }}>
                            {currency} {amount}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Código Promocional */}
                      <Grid item xs={12} md={3}>
                        <Paper elevation={1} sx={{ p: 2, bgcolor: promoCode ? '#e8f5e9' : 'white', border: '1px solid #e0e0e0', minHeight: '100%' }}>
                          {!promoCode ? (
                            <FormControl fullWidth>
                              <FormLabel sx={{ mb: 0.5, fontSize: '0.8rem', fontWeight: 600 }}>Código Promocional</FormLabel>
                              <TextField
                                size="small"
                                disabled={isValidatingPromo}
                                placeholder="Ingresar código"
                                value={promoInput}
                                onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                                error={!!promoError}
                                helperText={promoError}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Button onClick={applyPromoCode} disabled={isValidatingPromo || !promoInput} size="small" variant="contained">
                                        Aplicar
                                      </Button>
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </FormControl>
                          ) : (
                            <Box display="flex" justifyContent="space-between" alignItems="center" height="100%">
                              <Box>
                                <Typography variant="caption" color="success.main" display="block" fontWeight="bold">
                                  Código Aplicado:
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                  <LocalOfferIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} color="success" />
                                  {promoCode.code}
                                </Typography>
                                <Typography variant="caption" color="success.main">
                                  -{currency} {discountAmount.toFixed(2)}
                                </Typography>
                              </Box>
                              <IconButton size="small" color="error" onClick={removePromoCode}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Paper>
                      </Grid>

                      {/* Total a Pagar */}
                      <Grid item xs={12} md={2}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Total a Pagar
                          </Typography>
                          <Typography variant="h5" fontWeight="bold" color="success.main">
                            {currency} {finalAmount.toFixed(2)}
                          </Typography>
                        </Paper>
                      </Grid>

                      {/* Anticipo */}
                      <Grid item xs={12} md={2}>
                        <FormControl fullWidth>
                          <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
                            <AttachMoneyIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                            Anticipo
                          </FormLabel>
                          <TextField
                            name="advanceAmount"
                            type="number"
                            value={values.advanceAmount}
                            onChange={(e) => {
                              const inputAdvanceAmount = parseFloat(e.target.value) || 0;
                              handleTotalAmount(inputAdvanceAmount, finalAmount, setFieldValue);
                            }}
                            placeholder="0.00"
                            size="small"
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            helperText={showSplitPayments && getTotalPaid() > 0 ? `Total distribuido: $${getTotalPaid().toFixed(2)}` : ''}
                          />
                        </FormControl>
                      </Grid>

                      {/* Monto Restante */}
                      <Grid item xs={12} md={2}>
                        <Paper elevation={2} sx={{ p: 2, textAlign: 'center', bgcolor: '#fff3e0' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Restante
                          </Typography>
                          <Typography variant="h5" fontWeight="bold" color="warning.main">
                            {currency} {(finalAmount - (parseFloat(values.advanceAmount) || 0)).toFixed(2)}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 4 }} />

                  {/* ========== RESUMEN FINAL DE LA CONVERSIÓN ========== */}
                  <Paper elevation={3} sx={{ p: 3, bgcolor: '#f8f9fa' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Resumen de la Conversión
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Habitación:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" fontWeight="medium">
                          {values.roomNo} ({values.roomType})
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Cliente:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" fontWeight="medium">
                          {customerInfo.firstName} {customerInfo.lastName}
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Monto Total:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" fontWeight="bold" color="primary.main">
                          {currency} {finalAmount.toFixed(2)}
                        </Typography>
                      </Grid>

                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Anticipo:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {values.advanceAmount ? `${currency} ${values.advanceAmount}` : 'No especificado'}
                        </Typography>
                      </Grid>

                      {showSplitPayments && splitPayments.length > 0 && getTotalPaid() > 0 && (
                        <>
                          <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" fontWeight="bold" color="success.main" gutterBottom>
                              💰 Métodos de Pago:
                            </Typography>
                          </Grid>
                          {splitPayments
                            .filter((p) => p.amount && parseFloat(p.amount) > 0)
                            .map((payment, idx) => (
                              <React.Fragment key={payment.id}>
                                <Grid item xs={8}>
                                  <Typography variant="caption" color="text.secondary">
                                    •{' '}
                                    {payment.paymentMethod === 'cash'
                                      ? 'Efectivo'
                                      : payment.paymentMethod === 'card'
                                        ? 'Tarjeta'
                                        : payment.paymentMethod === 'transfer'
                                          ? 'Transferencia'
                                          : payment.paymentMethod === 'zelle'
                                            ? 'Zelle'
                                            : 'Otro'}{' '}
                                    ({payment.currency})
                                  </Typography>
                                </Grid>
                                <Grid item xs={4} textAlign="right">
                                  <Typography variant="caption" fontWeight="medium">
                                    ${payment.amount}
                                    {payment.currency !== 'USD' && (
                                      <Typography component="span" variant="caption" color="text.secondary" display="block">
                                        ({payment.currency} {payment.amountInForeignCurrency.toFixed(2)})
                                      </Typography>
                                    )}
                                  </Typography>
                                </Grid>
                              </React.Fragment>
                            ))}
                        </>
                      )}

                      {!showSplitPayments && values.paymentMethod && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Método de Pago:
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" fontWeight="medium">
                              {values.paymentMethod === 'cash'
                                ? '💵 Efectivo'
                                : values.paymentMethod === 'card'
                                  ? '💳 Tarjeta'
                                  : values.paymentMethod === 'transfer'
                                    ? '🏦 Transferencia'
                                    : values.paymentMethod === 'zelle'
                                      ? '💸 Zelle'
                                      : values.paymentMethod === 'other'
                                        ? '📝 Otro'
                                        : 'No especificado'}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Paper>
                </Paper>

                {/* Botones de acción */}
                <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                  <Button onClick={handleClose} disabled={formSubmitting}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="success"
                    disabled={
                      formSubmitting ||
                      !values.roomNo ||
                      !values.roomType ||
                      amount <= 0 ||
                      (showSplitPayments
                        ? !values.advanceAmount || parseFloat(values.advanceAmount || 0) <= 0 || !areSplitPaymentsValid(values.advanceAmount)
                        : !values.paymentMethod)
                    }
                    startIcon={formSubmitting ? <CircularProgress size={20} /> : <HotelIcon />}
                  >
                    {formSubmitting ? 'Convirtiendo...' : 'Confirmar Reserva'}
                  </Button>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </DialogContent>

      <MuiDialog open={conflictDialogOpen} onClose={() => setConflictDialogOpen(false)}>
        <MuiDialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'warning.light', color: 'warning.dark' }}>
          <InfoIcon /> Conflicto de Identidad Detectado
        </MuiDialogTitle>
        <MuiDialogContent sx={{ mt: 2 }}>
          <DialogContentText paragraph>
            El documento <strong>{conflictData?.preCustomer?.idCardType} {conflictData?.preCustomer?.idCard}</strong> ya existe en nuestra base de datos, pero con un nombre diferente al de esta pre-reserva.
          </DialogContentText>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Paper elevation={1} sx={{ p: 2, borderLeft: '4px solid #1976d2' }}>
              <Typography variant="subtitle2" color="primary">Datos en Base de Datos (Cliente Frecuente)</Typography>
              <Typography variant="body2"><strong>Nombre:</strong> {conflictData?.dbCustomer?.firstName} {conflictData?.dbCustomer?.lastName}</Typography>
              <Typography variant="body2"><strong>Teléfono:</strong> {conflictData?.dbCustomer?.phoneNumber}</Typography>
              <Typography variant="body2"><strong>Documento:</strong> {conflictData?.dbCustomer?.idCardType} {conflictData?.dbCustomer?.idcardNumber}</Typography>
            </Paper>
            <Paper elevation={1} sx={{ p: 2, borderLeft: '4px solid #ed6c02' }}>
              <Typography variant="subtitle2" color="warning.main">Datos de la Pre-reserva (Nueva información)</Typography>
              <Typography variant="body2"><strong>Nombre:</strong> {conflictData?.preCustomer?.firstName} {conflictData?.preCustomer?.lastName}</Typography>
              <Typography variant="body2"><strong>Teléfono:</strong> {conflictData?.preCustomer?.phone}</Typography>
              <Typography variant="body2"><strong>Documento:</strong> {conflictData?.preCustomer?.idCardType} {conflictData?.preCustomer?.idCard}</Typography>
            </Paper>
          </Box>
          <DialogContentText sx={{ mt: 2, fontWeight: 'medium' }}>
            ¿Qué información desea mantener en el sistema para este documento de identidad?
          </DialogContentText>
        </MuiDialogContent>
        <MuiDialogActions sx={{ p: 3, flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
            <Button
              onClick={() => {
                setConflictDialogOpen(false);
                convertToReservation(pendingSubmitValues, pendingResetForm, 'keep_existing');
              }}
              color="primary"
              variant="outlined"
              sx={{ flex: 1, py: 1.5 }}
            >
              Mantener Base de Datos
            </Button>
            <Button
              onClick={() => {
                setConflictDialogOpen(false);
                convertToReservation(pendingSubmitValues, pendingResetForm, 'overwrite');
              }}
              color="warning"
              variant="contained"
              sx={{ flex: 1, py: 1.5 }}
            >
              Sobrescribir con Pre-reserva
            </Button>
          </Box>
          <Button onClick={() => setConflictDialogOpen(false)} color="inherit" sx={{ width: '100%' }}>
            Cancelar Conversión
          </Button>
        </MuiDialogActions>
      </MuiDialog>
    </Dialog>
  );
};

ConvertPreReservation.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  preReservationData: PropTypes.object,
  onSuccess: PropTypes.func
};

export default ConvertPreReservation;
